import { useEffect, useRef, useState } from "react";
import * as api from "../../lib/claimsApi.ts";
import type { ExplanationResult } from "../../types/claims";
import { useLocale } from "../../context/LocaleProvider";

interface UseExplanationResult {
  explanation: ExplanationResult | null;
  isLoading: boolean;
  error: boolean;
}

/** Fetches the explanation paragraph for the given verification, in the reader's locale (fetched
 *  again when it changes). Gated on the
 *  fallacy step having completed (server returns 409 if called too early); the
 *  caller passes `ready` to express that gate. Errors are surfaced as a flag. */
export function useExplanation(
  userEventVerificationId: string | undefined,
  ready: boolean
): UseExplanationResult {
  const [explanation, setExplanation] = useState<ExplanationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const { locale } = useLocale();
  const fetchedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!userEventVerificationId || !ready) return;
    const key = `${userEventVerificationId}|${locale}`;
    if (fetchedFor.current === key) return;
    fetchedFor.current = key;
    setIsLoading(true);
    setError(false);
    api
      .fetchExplanation(userEventVerificationId, locale)
      .then((result) => {
        if (fetchedFor.current === key) setExplanation(result);
      })
      .catch(() => {
        if (fetchedFor.current === key) setError(true);
      })
      .finally(() => {
        if (fetchedFor.current === key) setIsLoading(false);
      });
  }, [userEventVerificationId, ready, locale]);

  return { explanation, isLoading, error };
}
